export interface KarelPagingRequest {
  orderBy?: {
    accessor: string;
    columnName: string;
    direction: 'ASC' | 'DESC';
  }[];
  page: number;
  rowSize: number;
}

export interface KarelMissedCallRequest {
  diagramName?: string;
  endDateTime: string;
  onlyNotCallback?: boolean;
  paging?: KarelPagingRequest;
  pressKeys?: boolean;
  ringAgent?: boolean;
  startDateTime: string;
}

export interface KarelMissedCallData {
  assigmentDate: string | null;
  assignmentInternalId: number;
  callId: string;
  callPhoneNumber: string;
  callStartDate: string;
  diagramName: string;
  fullCount: number;
  hangupId: number;
  missedCallReturnStatus: string | null;
  pressKeys: boolean;
  returnCallStartTime: string | null;
  returnPhoneNumber: string | null;
  ringAgent: boolean;
  trunkId: string;
}

export interface KarelMissedCallResponse {
  data: KarelMissedCallData[];
  message: string;
  params: Record<string, string>;
  resultId: number;
}

export class KarelApiService {
  private baseUrl: string;
  private tokenId: string;
  private adminTokenId: string | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_KAREL_API_BASE_URL || '';
    this.tokenId = import.meta.env.VITE_KAREL_API_TOKEN || '';

    if (!this.baseUrl || !this.tokenId) {
      console.warn('Karel API credentials not configured');
    }
  }

  async getAdminToken(): Promise<string> {
    if (this.adminTokenId) {
      return this.adminTokenId;
    }

    const { supabase } = await import('../lib/supabase');
    const { data } = await supabase
      .from('profiles')
      .select('karel_token')
      .eq('email', 'cem@pisaedu.com')
      .maybeSingle();

    if (data?.karel_token) {
      this.adminTokenId = data.karel_token;
      return this.adminTokenId;
    }

    throw new Error('Admin Karel token bulunamadı. Cem ENGÜLEÇ\'in Karel token\'ını profiles tablosuna eklemelisiniz.');
  }

  async getMissedCallReport(
    startDate: Date,
    endDate: Date,
    options?: {
      diagramName?: string;
      onlyNotCallback?: boolean;
      pressKeys?: boolean;
      ringAgent?: boolean;
      page?: number;
      rowSize?: number;
    }
  ): Promise<KarelMissedCallResponse> {
    if (!this.baseUrl) {
      throw new Error('Karel API yapılandırılmamış! .env dosyasında VITE_KAREL_API_BASE_URL değerini ayarlayın.');
    }

    if (this.baseUrl.includes('your-karel-api-url')) {
      throw new Error('Karel API bilgileri placeholder değerler içeriyor! .env dosyasını gerçek API bilgileriyle güncelleyin.');
    }

    const adminToken = await this.getAdminToken();

    const request: KarelMissedCallRequest = {
      startDateTime: startDate.toISOString(),
      endDateTime: endDate.toISOString(),
      diagramName: options?.diagramName,
      onlyNotCallback: options?.onlyNotCallback ?? false,
      pressKeys: options?.pressKeys,
      ringAgent: options?.ringAgent,
      paging: {
        orderBy: [
          {
            accessor: 'callStartDate',
            columnName: 'callStartDate',
            direction: 'DESC',
          },
        ],
        page: options?.page ?? 0,
        rowSize: options?.rowSize ?? 100,
      },
    };

    try {
      const response = await fetch(`${this.baseUrl}/token/reports/getmissedcallreport2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'TokenId': adminToken,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Karel API error: ${response.status} - ${errorText}`);
      }

      const data: KarelMissedCallResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching missed call report:', error);
      throw error;
    }
  }

  async getMissedCallsLast24Hours(): Promise<KarelMissedCallResponse> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);

    return this.getMissedCallReport(startDate, endDate, {
      onlyNotCallback: true,
      rowSize: 100,
    });
  }

  async getMissedCallsLastWeek(): Promise<KarelMissedCallResponse> {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

    return this.getMissedCallReport(startDate, endDate, {
      rowSize: 500,
    });
  }

  async getMissedCallsDateRange(startDate: Date, endDate: Date, onlyNotCallback: boolean = false): Promise<KarelMissedCallResponse> {
    return this.getMissedCallReport(startDate, endDate, {
      onlyNotCallback,
      rowSize: 1000,
    });
  }

  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.startsWith('90') && cleaned.length === 12) {
      return `+90 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10, 12)}`;
    }

    if (cleaned.startsWith('5') && cleaned.length === 10) {
      return `+90 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
    }

    if (cleaned.startsWith('0') && cleaned.length === 11) {
      return `+90 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9, 11)}`;
    }

    return phoneNumber;
  }

  normalizePhoneNumber(phoneNumber: string): string {
    let cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.startsWith('90')) {
      cleaned = cleaned.slice(2);
    } else if (cleaned.startsWith('0')) {
      cleaned = cleaned.slice(1);
    }

    if (cleaned.length === 10 && cleaned.startsWith('5')) {
      return cleaned;
    }

    return phoneNumber;
  }
}

export const karelApiService = new KarelApiService();
